import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  inject,
  signal,
  AfterViewChecked,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { ResponseCardComponent } from '../response-card/colimind-response-card.component';
import { AgentQuestionsComponent } from '../agent-questions/colimind-agent-questions.component';
import { ColimindDebugService } from '../../../services/colimind/colimind-debug.service';
import type { Message } from '../colimind-types';
import { formatMessageTime } from '../colimind-utils';

@Component({
  selector: 'colimo-chat-window',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    TranslateModule,
    ResponseCardComponent,
    AgentQuestionsComponent,
  ],
  templateUrl: './colimind-chat-window.component.html',
  styleUrls: ['./colimind-chat-window.component.scss'],
})
export class ChatWindowComponent implements AfterViewChecked, OnChanges {
  readonly debug = inject(ColimindDebugService);
  @Input() messages: Message[] = [];
  @Input() loading = false;
  /** Translation key shown next to the loading dots. */
  @Input() status: string | null = null;
  /** Pulses the input once, right after the onboarding, to show where to start. */
  @Input() highlightInput = false;
  @Output() send = new EventEmitter<string>();
  @Output() stop = new EventEmitter<void>();
  @Output() saveAsSuggestion = new EventEmitter<string>();
  @Output() answerQuestions = new EventEmitter<{ message: Message; answers: string[] }>();

  @ViewChild('scrollEl') scrollEl?: ElementRef<HTMLElement>;
  @ViewChild('inputEl') private inputEl?: ElementRef<HTMLTextAreaElement>;

  /** Lets the page put the caret here, e.g. as the cue right after the onboarding. */
  focusInput(): void {
    this.inputEl?.nativeElement.focus();
  }

  input = '';
  showScrollButton = false;
  unreadCount = 0;
  private shouldScroll = false;
  private static readonly BOTTOM_THRESHOLD = 80;

  ngAfterViewChecked(): void {
    // Only moves the view; changing bound state here would fail the change detection check
    if (this.shouldScroll) {
      this.scrollEl?.nativeElement.scrollTo({ top: this.scrollEl.nativeElement.scrollHeight, behavior: 'smooth' });
      this.shouldScroll = false;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['messages']) return;

    const prev: Message[] = changes['messages'].previousValue ?? [];
    const curr = this.messages;

    // Replacing the list (session switch) keeps no existing message objects, so a
    // changed first element distinguishes a replace from an append.
    if (curr.length < prev.length || (prev.length > 0 && prev[0] !== curr[0])) {
      this.showScrollButton = false;
      this.unreadCount = 0;
      this.shouldScroll = true;
      return;
    }

    // Read the position before the new content grows the container, otherwise it
    // always measures as "not at bottom".
    const atBottom = this.isNearBottom();
    this.shouldScroll = this.shouldScroll || atBottom;
    if (curr.length > prev.length && !atBottom) {
      this.unreadCount += curr.length - prev.length;
    }
  }

  onScroll(): void {
    const atBottom = this.isNearBottom();
    this.showScrollButton = !atBottom;
    if (atBottom) this.unreadCount = 0;
  }

  scrollToBottom(): void {
    const el = this.scrollEl?.nativeElement;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    this.showScrollButton = false;
    this.unreadCount = 0;
  }

  private isNearBottom(): boolean {
    const el = this.scrollEl?.nativeElement;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < ChatWindowComponent.BOTTOM_THRESHOLD;
  }

  readonly copiedMessage = signal<Message | null>(null);

  async copyQuestion(msg: Message): Promise<void> {
    await navigator.clipboard.writeText(msg.text);
    this.copiedMessage.set(msg);
    setTimeout(() => this.copiedMessage.update(m => (m === msg ? null : m)), 2000);
  }

  messageTime(msg: Message): string {
    return formatMessageTime(msg.timestamp);
  }

  submit(): void {
    const q = this.input.trim();
    if (!q || this.loading) return;
    this.input = '';
    this.showScrollButton = false;
    this.unreadCount = 0;
    this.shouldScroll = true;
    this.send.emit(q);
  }

  onEnter(event: Event): void {
    const ke = event as KeyboardEvent;
    if (!ke.shiftKey) {
      ke.preventDefault();
      this.submit();
    }
  }

  autoResize(event: Event): void {
    const el = event.target as HTMLTextAreaElement;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 160) + 'px';
  }
}
